import { dateTimeFormat, todayWithSystemTimeZone } from '@magicdoorinc/date';
import { useForm } from '@magicdoorinc/form';
import { currency, toast, useLocalization, createMutation } from '@magicdoorinc/ui';
import { createEffect, createMemo, createSignal } from 'solid-js';
import { useAuth } from '~/contexts/auth/AuthContext';
import { useCurrentCompany } from '~/contexts/companies/CompaniesContext';
import { useCompanyBusinessInfo } from '~/contexts/company-business-info/BusinessInfoContext';
import { useSubscription } from '~/contexts/subscription/SubscriptionContext';
import { createMagicDoorContext } from '~/contexts/utils';
import { formatCardProcessingFee } from '~/pages/bank-accounts/bank-account-details/components/formatCardProcessingFee';
import {
  accountDefaultsFromPmAndCompany,
  cloneLinkAccountDetailsFormData,
  emptyAccountForm,
  emptyAgreementForm,
  normalizeLinkAccountDetailsFormData,
  toRequestConnectedAccountPayload,
} from '~/pages/bank-accounts/bank-account-details/components/linkMagicDoorPaymentsForm';
import { payPaymentMethodsRepository } from '~/repositories/payPaymentMethodsRepository';
import { resolveDefaultBusinessInfoId } from '~/utils/resolveDefaultBusinessInfoId';
import type { ParentProps } from 'solid-js';
import type { LinkAccountDetailsFormData } from '~/pages/bank-accounts/bank-account-details/components/linkMagicDoorPaymentsForm';
import type { RequestConnectedAccountRequestDto } from '~/swagger/pay/Api';

type LinkMagicDoorPaymentsProviderProps = {
  bankAccountId: () => string;
  open: () => boolean;
  onRefetchBusinessInfo?: () => void;
  onComplete?: () => void;
  onClose?: () => void;
};

export const [LinkMagicDoorPaymentsProvider, useLinkMagicDoorPayments] = createMagicDoorContext(
  'LinkMagicDoorPayments',
  (props: ParentProps<LinkMagicDoorPaymentsProviderProps>) => {
    const { t } = useLocalization();
    const subscriptionResource = useSubscription();
    const { companyBusinessInfoList, companyBusinessInfoListActions } = useCompanyBusinessInfo();
    const { currentCompany } = useCurrentCompany();
    const { me } = useAuth();

    const [step, setStep] = createSignal<1 | 2 | 3>(1);
    const [expandedQa, setExpandedQa] = createSignal<string | undefined>();
    const [accountSnapshot, setAccountSnapshot] = createSignal<LinkAccountDetailsFormData | undefined>();
    const [lastSubmittedAccountSnapshot, setLastSubmittedAccountSnapshot] = createSignal<LinkAccountDetailsFormData | undefined>();
    const [connectedAccountAcceptedDate, setConnectedAccountAcceptedDate] = createSignal('');

    const buildOpenAccountValues = () => {
      return normalizeLinkAccountDetailsFormData({
        ...lastSubmittedAccountSnapshot(),
        documentUploadSessionIds: [],
      });
    };

    const accountForm = useForm({
      initialValues: emptyAccountForm(),
    });

    const agreementForm = useForm({
      initialValues: emptyAgreementForm(),
    });

    const resetAll = () => {
      setStep(1);
      accountForm.setFieldsValue(buildOpenAccountValues());
      accountForm.setFieldValue('documentUploadSessionIds', [], false);
      agreementForm.resetFields();
      setAccountSnapshot(undefined);
      setConnectedAccountAcceptedDate('');
      setExpandedQa(undefined);
      hasFilledAccountStepDefaults = false;
      hasSeededMultiBusinessInfoId = false;
    };

    createEffect(() => {
      if (props.open()) {
        companyBusinessInfoListActions.refetch();
        props.onRefetchBusinessInfo?.();
        accountForm.setFieldsValue(buildOpenAccountValues());
        accountForm.setFieldValue('documentUploadSessionIds', [], false);
        agreementForm.resetFields();
      }
    });

    createEffect(() => {
      if (!props.open()) {
        resetAll();
      }
    });

    const businessInfoListItems = createMemo(() => companyBusinessInfoList() ?? []);

    let hasFilledAccountStepDefaults = false;
    let hasSeededMultiBusinessInfoId = false;

    createEffect(() => {
      if (!props.open() || step() !== 2) {
        return;
      }
      if (hasFilledAccountStepDefaults) return;
      if (lastSubmittedAccountSnapshot()) {
        hasFilledAccountStepDefaults = true;
        return;
      }
      const profile = me();
      if (profile == null) return;
      accountForm.setFieldsValue(accountDefaultsFromPmAndCompany(profile, currentCompany()));
      hasFilledAccountStepDefaults = true;
    });

    createEffect(() => {
      if (!props.open() || step() !== 2) {
        return;
      }
      const items = businessInfoListItems();
      if (items.length === 0) {
        return;
      }
      if (lastSubmittedAccountSnapshot()?.companyBusinessInfoId) {
        hasSeededMultiBusinessInfoId = true;
        return;
      }
      if (items.length === 1) {
        accountForm.setFieldsValue({ companyBusinessInfoId: items[0]!.id });
        return;
      }
      if (hasSeededMultiBusinessInfoId) return;
      const defaultBusinessInfoId = resolveDefaultBusinessInfoId(items);
      if (!defaultBusinessInfoId) return;
      accountForm.setFieldsValue({ companyBusinessInfoId: defaultBusinessInfoId });
      hasSeededMultiBusinessInfoId = true;
    });

    const modalTitle = createMemo(() => {
      const s = step();
      if (s === 1) return t('Pricing information');
      if (s === 2) return t('Compliance information');
      if (s === 3) return t('E-Signature agreement');
      return t('Pricing information');
    });

    const subscriptionLoading = () => subscriptionResource.effectiveSubscription.loading;

    const subscription = createMemo(() => subscriptionResource.effectiveSubscription());

    const formatTransactionFeeLine = (percent: number, fixedAmount: number) => {
      const fee = formatCardProcessingFee(percent, fixedAmount);
      return fee ? t('{fee} per transaction', { fee }) : null;
    };

    const creditCardFeeLine = createMemo(() => {
      const sub = subscription();
      if (!sub) return null;
      return formatTransactionFeeLine(sub.cardProcessingCreditPercent, sub.cardProcessingCreditFixedAmount);
    });

    const debitCardFeeLine = createMemo(() => {
      const sub = subscription();
      if (!sub) return null;
      return formatTransactionFeeLine(sub.cardProcessingDebitPercent, sub.cardProcessingDebitFixedAmount);
    });

    const amexCreditFeeLine = createMemo(() => {
      const sub = subscription();
      if (!sub) return null;
      return formatTransactionFeeLine(sub.cardProcessingAmexCreditPercent, sub.cardProcessingAmexCreditFixedAmount);
    });

    const businessCreditFeeLine = createMemo(() => {
      const sub = subscription();
      if (!sub) return null;
      return formatTransactionFeeLine(sub.cardProcessingBusinessCreditPercent, sub.cardProcessingBusinessCreditFixedAmount);
    });

    const exemptDebitFeeLine = createMemo(() => {
      const sub = subscription();
      if (!sub) return null;
      return formatTransactionFeeLine(sub.cardProcessingExemptDebitPercent, sub.cardProcessingExemptDebitFixedAmount);
    });

    const businessDebitFeeLine = createMemo(() => {
      const sub = subscription();
      if (!sub) return null;
      return formatTransactionFeeLine(sub.cardProcessingBusinessDebitPercent, sub.cardProcessingBusinessDebitFixedAmount);
    });

    const achFeeLine = createMemo(() => {
      const sub = subscription();
      if (!sub || sub.tenantTransactionFee == null) return null;
      return t('{fee} per transaction', { fee: currency(sub.tenantTransactionFee) });
    });

    const validateAccountStep = (): boolean => {
      const items = businessInfoListItems();
      const { validateStatus } = accountForm.validateForm();
      if (items.length === 0) {
        return false;
      }
      return validateStatus;
    };

    const buildPayload = (): RequestConnectedAccountRequestDto | null => {
      const snap = accountSnapshot();
      if (!snap) return null;
      const accepted = connectedAccountAcceptedDate();
      if (!accepted) return null;
      const form = normalizeLinkAccountDetailsFormData(snap);
      if (!form.companyBusinessInfoId) return null;
      return toRequestConnectedAccountPayload({
        bankAccountId: props.bankAccountId(),
        form,
        accepted,
      });
    };

    const goToAccountStep = () => {
      setStep(2);
    };

    const handleNextFromAccountStep = () => {
      if (!validateAccountStep()) return;
      setConnectedAccountAcceptedDate(dateTimeFormat(todayWithSystemTimeZone(), { formatterTemplate: 'api', timeFormat: false }));
      setAccountSnapshot(cloneLinkAccountDetailsFormData(accountForm.formStore as Partial<LinkAccountDetailsFormData>));
      setStep(3);
    };

    const goBackToAccountStep = () => {
      const snap = accountSnapshot();
      if (snap) {
        accountForm.setFieldsValue(snap);
      }
      setStep(2);
    };

    const requestConnectedAccountMutation = createMutation(async (body: RequestConnectedAccountRequestDto) => {
      return payPaymentMethodsRepository.requestConnectedAccount(body);
    });

    const handleSubmit = async () => {
      const { validateStatus } = agreementForm.validateForm();
      if (!validateStatus) return;
      const body = buildPayload();
      if (!body) {
        toast.error(t('Could not submit. Check your information and try again.'));
        return;
      }
      try {
        await requestConnectedAccountMutation(body);
        setLastSubmittedAccountSnapshot(
          normalizeLinkAccountDetailsFormData({
            ...accountSnapshot(),
            documentUploadSessionIds: [],
          })
        );
        props.onComplete?.();
        props.onClose?.();
        resetAll();
      } catch (e) {
        console.error(e);
        toast.error(t('Could not submit. Check your information and try again.'));
      }
    };

    const toggleQa = (key: string) => {
      setExpandedQa((c) => (c === key ? undefined : key));
    };

    const signerName = () => accountSnapshot()?.signerName ?? '';

    const businessInfoOptions = createMemo(() => {
      return businessInfoListItems().map((item) => ({
        label: item.displayLabel,
        value: item.id,
      }));
    });

    return {
      accountForm,
      agreementForm,
      step,
      setStep,
      expandedQa,
      connectedAccountAcceptedDate,
      modalTitle,
      subscriptionLoading,
      subscription,
      creditCardFeeLine,
      debitCardFeeLine,
      amexCreditFeeLine,
      businessCreditFeeLine,
      exemptDebitFeeLine,
      businessDebitFeeLine,
      achFeeLine,
      validateAccountStep,
      buildPayload,
      goToAccountStep,
      handleNextFromAccountStep,
      goBackToAccountStep,
      handleSubmit,
      toggleQa,
      signerName,
      resetAll,
      requestConnectedAccount: requestConnectedAccountMutation,
      businessInfoOptions,
    };
  }
);
